import {PageState, SortState, ColumnIds, IdName, DateModes, DemandSourceRecord} from "../utils/types";
import StateHelper from "../utils/StateHelper";
import { DatagridHelper } from "../datagrid/DatagridHelper";
import dayjs from '../utils/dayjs';

export function isDateGroupBy(id : string) : boolean {
  return id === ColumnIds.date || id === ColumnIds.week || id === ColumnIds.month;
}

/**
 * Ensure only a single date period group by (date, week, month) is selected at a time,
 * giving preference to the new one selected.
 * 
 * Takes the new selection (from the type ahead box), the existing selection (from the pageState)
 * and returns the new selection, but with any extra date period groupBys removed. 
 * 
 * If more than 2 date groupBys are somehow selected, then the first one is kept and the others are removed.
 */
export function toggleDateGroupBys(newSelection: IdName[], existingSelection: string): IdName[] {
  const existingDateGroupBys = (existingSelection || "").split(",").filter( gb => isDateGroupBy(gb) );

  // figure out what date groupBys are being selected, and which ones weren't previously selected
  const dateGroupBys = newSelection.filter( val => isDateGroupBy(val.id) ).map( val => val.id );
  const newDateGroupBys = dateGroupBys.filter( val => existingDateGroupBys.indexOf(val) === -1);

  // no date group bys - nothing to change
  if ( dateGroupBys.length === 0 ) {
    return newSelection;
  }

  // if any of the date groupBys are new, return that one (plus all non-date groupBys)
  if ( newDateGroupBys.length >= 1 ) {
    // only keep the first newGroupBy value and remove the other dateGroupBys
    return newSelection.filter( val => !isDateGroupBy(val.id) || val.id === newDateGroupBys[0] );
  }

  // otherwise, all of the date groupBys were already selected, so just keep the first one
  return newSelection.filter( val => !isDateGroupBy(val.id) || val.id === dateGroupBys[0] );
}

export const FiltersEventsHelper = {
  groupByOnChange : function(page :string, pageState : PageState, sortState:SortState,  selectedValues: IdName[]) {

    selectedValues = toggleDateGroupBys(selectedValues, pageState.groupBy);

    if (selectedValues && selectedValues.length > 0) {
      if (selectedValues.find((column) => column.id === ColumnIds.date) && !pageState.groupBy.includes(ColumnIds.date)) {
        sortState.columnIdSorted = ColumnIds.date;
        sortState.sort = 1;
      } else if (selectedValues.find((column) => column.id === ColumnIds.week) && !pageState.groupBy.includes(ColumnIds.week)) {
        sortState.columnIdSorted = ColumnIds.week;
        sortState.sort = 1;
      } else if (selectedValues.find((column) => column.id === ColumnIds.month) && !pageState.groupBy.includes(ColumnIds.month)) {
        sortState.columnIdSorted = ColumnIds.month;
        sortState.sort = 1;
      }


      let groupBy = "";
      for (let i = 0; i < selectedValues.length; i++) {
        if( i > 0 ){
          groupBy += ",";
        }
        const element = selectedValues[i];
        groupBy += element.id;
      }
      pageState.groupBy = groupBy;
    } else {
      pageState.groupBy = null;
    }    
    if (pageState.pivotBy !== null) {
      const isOnPivotSelection = false;
      const { newGroupBy } = DatagridHelper.checkPivotCombinationWithGroupsByAndDates(pageState.pivotBy, pageState.groupBy, pageState.dateStart, pageState.dateEnd, isOnPivotSelection, pageState.dateMode);
      pageState.groupBy = newGroupBy;
    }

    const searchFuntion = StateHelper.getSearchFuntion(page);
    searchFuntion();
  },

  dateRangeOnChange : function(page :string,pageState : PageState, sortState:SortState, start: dayjs.Dayjs, end: dayjs.Dayjs, dateMode: DateModes){
    pageState.dateStart = start;
    pageState.dateEnd = end;
    const isOnPivotSelection = false;
    const { newGroupBy, newDateStart, newDateEnd, newPivotBy, newDateMode } = DatagridHelper.checkPivotCombinationWithGroupsByAndDates(pageState.pivotBy, pageState.groupBy, pageState.dateStart, pageState.dateEnd, isOnPivotSelection, dateMode);
 
    if (newGroupBy.includes(ColumnIds.date) && !pageState.groupBy.includes(ColumnIds.date)) {
      sortState.columnIdSorted = ColumnIds.date;
      sortState.sort = 1;
    } else if (newGroupBy.includes(ColumnIds.week) && !pageState.groupBy.includes(ColumnIds.week)) {
      sortState.columnIdSorted = ColumnIds.week;
      sortState.sort = 1;
    } else if (newGroupBy.includes(ColumnIds.month) && !pageState.groupBy.includes(ColumnIds.month)) {
      sortState.columnIdSorted = ColumnIds.month;
      sortState.sort = 1;
    }

    pageState.dateMode = newDateMode;
    pageState.dateStart = newDateStart;
    pageState.dateEnd = newDateEnd;
    pageState.pivotBy = newPivotBy;
    pageState.groupBy = newGroupBy;

    const searchFuntion = StateHelper.getSearchFuntion(page);
    searchFuntion();    
  },

  demandSorucesDropdownOnChange : function (page : string, pageState : PageState, selectedValues: DemandSourceRecord[], isDirectSet?: boolean){
    // Get current selected demand sources from pageState
    const currentSelectedIds = pageState.demandSourceIds ? pageState.demandSourceIds.split(",") : [];
    const allDemandSources = StateHelper.getDemandSourcesState() || [];

    // Rebuild current selected objects from IDs
    const currentDemandSources = currentSelectedIds.map(id =>
      allDemandSources.find(ds => ds.demandSourceId === id)
    ).filter(ds => ds !== undefined) as DemandSourceRecord[];

    // If explicitly marked as direct set (from X buttons), or empty array
    if (isDirectSet || selectedValues.length === 0) {
      pageState.demandSourceIds = selectedValues.length > 0 ? selectedValues.map(d => d.demandSourceId).join(",") : null;
      const searchFuntion = StateHelper.getSearchFuntion(page);
      searchFuntion();
      return;
    }

    // Use toggle pattern for checkbox clicks (similar to getGroupedDemandSourcesCard)
    const newDemandSources = [...currentDemandSources];

    selectedValues.forEach((selectedValue: DemandSourceRecord) => {
      // Always check against the original array
      const index = currentDemandSources.findIndex((existing: DemandSourceRecord) => existing.demandSourceId === selectedValue.demandSourceId);

      if (index !== -1) {
        // Item exists in original, remove it from current
        const currentIndex = newDemandSources.findIndex((existing: DemandSourceRecord) => existing.demandSourceId === selectedValue.demandSourceId);
        if (currentIndex !== -1) {
          newDemandSources.splice(currentIndex, 1);
        }
      } else {
        // Item doesn't exist in original, add it if not already added
        const alreadyAdded = newDemandSources.findIndex((existing: DemandSourceRecord) => existing.demandSourceId === selectedValue.demandSourceId);
        if (alreadyAdded === -1) {
          newDemandSources.push(selectedValue);
        }
      }
    });

    pageState.demandSourceIds = newDemandSources.length > 0 ? newDemandSources.map(d => d.demandSourceId).join(",") : null;

    const searchFuntion = StateHelper.getSearchFuntion(page);
    searchFuntion();
  }
};
